import { useCallback, useRef, useState } from "react";

/**
 * useBlockDrag — encapsulates pointer-drag reorder logic for blocks.
 *
 * Returns:
 *   onHandlePointerDown(blockId, e) — attach to the drag handle's onPointerDown.
 *   dropLineBeforeId               — id of the block the drop-line should appear before
 *                                    (null = append at end, undefined = no drag active).
 *
 * Calls onReorder(fromId, beforeId) when the user releases.
 * No JSX — pure hook.
 */
export interface UseBlockDragResult {
	onHandlePointerDown: (blockId: string, e: React.PointerEvent) => void;
	dropLineBeforeId: string | null | undefined;
}

export function useBlockDrag(
	onReorder: (fromId: string, beforeId: string | null) => void,
	getBlockEls: () => NodeListOf<Element> | Element[]
): UseBlockDragResult {
	// undefined = not dragging; null = dragging, drop at end; string = drop before that id
	const [dropLineBeforeId, setDropLineBeforeId] = useState<string | null | undefined>(undefined);

	const dragRef = useRef<{
		fromId: string;
		startX: number;
		startY: number;
		moved: boolean;
	} | null>(null);

	// Stable ref that tracks beforeId synchronously (avoids stale closure in onPointerUp)
	const beforeIdRef = useRef<string | null>(null);

	const stableOnPointerMove = useCallback(
		(e: PointerEvent) => {
			const drag = dragRef.current;
			if (!drag) return;
			if (!drag.moved) {
				if (Math.abs(e.clientX - drag.startX) + Math.abs(e.clientY - drag.startY) < 5) return;
				drag.moved = true;
				document.body.style.userSelect = "none";
			}
			const els = getBlockEls();
			let beforeId: string | null = null;
			for (let i = 0; i < els.length; i++) {
				const el = els[i] as HTMLElement;
				if (el.dataset.id === drag.fromId) continue;
				const rect = el.getBoundingClientRect();
				if (e.clientY < rect.top + rect.height / 2) {
					beforeId = el.dataset.id ?? null;
					break;
				}
			}
			beforeIdRef.current = beforeId;
			setDropLineBeforeId(beforeId);
		},
		[getBlockEls]
	);

	const stableOnPointerUp = useCallback(() => {
		document.removeEventListener("pointermove", stableOnPointerMove);
		document.removeEventListener("pointerup", stableOnPointerUp);
		document.body.style.userSelect = "";

		const drag = dragRef.current;
		dragRef.current = null;
		setDropLineBeforeId(undefined);

		if (!drag || !drag.moved) return;
		onReorder(drag.fromId, beforeIdRef.current);
	}, [stableOnPointerMove, onReorder]);

	const onHandlePointerDown = useCallback(
		(blockId: string, e: React.PointerEvent) => {
			if (e.button !== 0) return;
			e.preventDefault();
			e.stopPropagation();

			dragRef.current = { fromId: blockId, startX: e.clientX, startY: e.clientY, moved: false };
			beforeIdRef.current = null;

			document.addEventListener("pointermove", stableOnPointerMove);
			document.addEventListener("pointerup", stableOnPointerUp, { once: true });
		},
		[stableOnPointerMove, stableOnPointerUp]
	);

	return { onHandlePointerDown, dropLineBeforeId };
}
